/**
 * ConversationItem - single row in sidebar Recent list.
 *
 * MD-31 ASKLAW-EMBER-THEME Phase 2. Per spec F-FE-THEME-GEMINI §7.2.
 *
 * Layout:
 *   - padding 10px 16px
 *   - text-overflow ellipsis 1-line title
 *   - hover bg var(--asklaw-accent-soft) transition 140ms
 *   - active bg --asklaw-accent-soft + bold + left border 3px gradient
 *   - right icon MoreHorizontal reveal on hover (Rename / Delete / Share placeholder)
 *
 * Phase 2 keeps menu as placeholder (visual only). Real wiring (POST /api/v1/chat/.../rename
 * etc) defer to subsequent phase.
 */

import React, { useCallback } from 'react';
import { MoreHorizontalIcon, Trash2Icon } from './icons';

export type ConversationItemProps = {
  id: string;
  title: string;
  lastMessageAt?: string;
  isActive?: boolean;
  lang: 'vi' | 'en';
  onSelect: (id: string) => void;
  onDelete?: (id: string) => void;
};

export function ConversationItem({
  id,
  title,
  isActive = false,
  lang,
  onSelect,
  onDelete,
}: ConversationItemProps) {
  const handleClick = useCallback(() => {
    onSelect(id);
  }, [id, onSelect]);

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(id);
    },
    [id, onDelete]
  );

  return (
    <div
      className={[
        'conversation-item',
        isActive ? 'conversation-item--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="button"
      tabIndex={0}
      aria-current={isActive ? 'page' : undefined}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <span className="conversation-item__title">{title}</span>
      <div className="conversation-item__actions">
        {onDelete ? (
          <button
            type="button"
            className="conversation-item__action"
            onClick={handleDeleteClick}
            aria-label={
              lang === 'vi'
                ? `Xóa cuộc trò chuyện ${title}`
                : `Delete conversation ${title}`
            }
            title={lang === 'vi' ? 'Xóa' : 'Delete'}
          >
            <Trash2Icon size={14} strokeWidth={1.6} />
          </button>
        ) : (
          <button
            type="button"
            className="conversation-item__action"
            onClick={(e) => e.stopPropagation()}
            aria-label={lang === 'vi' ? 'Tùy chọn' : 'Options'}
            title={lang === 'vi' ? 'Tùy chọn' : 'Options'}
            disabled
          >
            <MoreHorizontalIcon size={14} strokeWidth={1.6} />
          </button>
        )}
      </div>
    </div>
  );
}

export default ConversationItem;
